import { produce } from 'immer';

import { hasRequired } from '@app/features/apply-react-signing/libs/fieldTypeGuards';
import {
  CONTRACT_PROGRESS_ADDRESS_FIELD_VALUE_CHANGE,
  CONTRACT_PROGRESS_COMPANY_NAME_FIELD_VALUE_CHANGE,
  CONTRACT_PROGRESS_CONTRACT_LOAD_SUCCESS,
  CONTRACT_PROGRESS_DATE_FIELD_VALUE_CHANGE,
  CONTRACT_PROGRESS_DROPDOWN_FIELD_VALUE_CHANGE,
  CONTRACT_PROGRESS_NAME_FIELD_VALUE_CHANGE,
  CONTRACT_PROGRESS_SIGNATURE_INSERT,
  CONTRACT_PROGRESS_SIGNATURE_INSERTION_RESET,
  CONTRACT_PROGRESS_SIGNATURE_SELECT,
  CONTRACT_PROGRESS_TEXT_FIELD_VALUE_CHANGE,
} from '@app/store/actionTypes';
import {
  ContractProgressAction,
  contractProgressCheckboxGroupCheckedCountChange,
  initializeContractProgress,
} from '@app/store/contract-progress/actions';

type ContractProgressCompletedIdsState = string[];

const initialState: ContractProgressCompletedIdsState = [];

export default function reducer(
  state: ContractProgressCompletedIdsState = initialState,
  action: ContractProgressAction
) {
  switch (action.type) {
    case contractProgressCheckboxGroupCheckedCountChange.type:
      return produce(state, (draft) => {
        const { id, required, count } = action.payload;
        const index = draft.indexOf(id);
        if (index === -1) {
          if (count >= 1 && required) {
            draft.push(id);
          }
        } else if (count === 0) {
          draft.splice(index, 1);
        }
      });
    case initializeContractProgress.type:
      return initialState;
    case CONTRACT_PROGRESS_SIGNATURE_INSERTION_RESET:
      return produce(state, (draft) => {
        const { signatureIds } = action.payload;
        return draft.filter((id) => signatureIds.indexOf(id) === -1);
      });
    case CONTRACT_PROGRESS_SIGNATURE_INSERT:
      return produce(state, (draft) => {
        draft.push(action.payload);
      });
    case CONTRACT_PROGRESS_SIGNATURE_SELECT:
      return produce(state, (draft) => {
        const { inputId } = action.payload;
        draft.push(inputId);
      });
    case CONTRACT_PROGRESS_DROPDOWN_FIELD_VALUE_CHANGE:
    case CONTRACT_PROGRESS_DATE_FIELD_VALUE_CHANGE:
      return produce(state, (draft) => {
        const { id, required, value } = action.payload;
        const index = draft.indexOf(id);
        if (index === -1) {
          if (value.trim() && required) {
            draft.push(id);
          }
        } else if (!value.trim()) {
          draft.splice(index, 1);
        }
      });
    case CONTRACT_PROGRESS_TEXT_FIELD_VALUE_CHANGE:
      return produce(state, (draft) => {
        const { id, required, value, isValid } = action.payload;
        const index = draft.indexOf(id);
        if (index === -1) {
          if ((value.trim() && required) || (!required && isValid)) {
            draft.push(id);
          }
        } else if (!value.trim()) {
          draft.splice(index, 1);
        }
      });
    case CONTRACT_PROGRESS_NAME_FIELD_VALUE_CHANGE:
    case CONTRACT_PROGRESS_COMPANY_NAME_FIELD_VALUE_CHANGE:
    case CONTRACT_PROGRESS_ADDRESS_FIELD_VALUE_CHANGE:
      return produce(state, (draft) => {
        const { fields, value } = action.payload;
        fields.forEach(({ id, required }) => {
          const index = draft.indexOf(id);
          if (index === -1) {
            if (value.trim() && required) {
              draft.push(id);
            }
          } else if (!value.trim()) {
            draft.splice(index, 1);
          }
        });
      });

    case CONTRACT_PROGRESS_CONTRACT_LOAD_SUCCESS: {
      return action.payload.fields
        .flatMap((f) => {
          if (f.type === 'AUTOFILL_GROUP') {
            return f.children;
          }
          return f;
        })
        .filter(
          (field) =>
            hasRequired(field.config) &&
            field.config.required &&
            'input' in field &&
            field.input &&
            'value' in field.input &&
            !!field.input.value
        )
        .map(({ id }) => id);
    }

    default:
      return state;
  }
}
